import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { authQueryKey, passkeyErrorKey, signIn } from '../../lib/auth.ts';
import { m } from '../../paraglide/messages.js';
import { Banner } from '../../ui/Banner.tsx';
import { Button } from '../../ui/Button.tsx';
import { TextField } from '../../ui/TextField.tsx';
import { AuthScreen, BrandMark, Wordmark } from './AuthScreen.tsx';
import { RecoverForm } from './RecoverForm.tsx';

/** design/reference/Login.dc.html, frame A. */
export function LoginScreen() {
  const queryClient = useQueryClient();
  const onSignedIn = () => queryClient.invalidateQueries({ queryKey: authQueryKey });
  const mutation = useMutation({ mutationFn: () => signIn(), onSuccess: onSignedIn });
  const supported = browserSupportsWebAuthn();
  const [recovering, setRecovering] = useState(false);

  // Conditional UI: offer saved passkeys in the field's autofill while the screen is open.
  useEffect(() => {
    let active = true;
    void browserSupportsWebAuthnAutofill().then((autofill) => {
      if (!active || !autofill) return;
      signIn(true).then(onSignedIn, () => undefined);
    });
    return () => {
      active = false;
      WebAuthnAbortService.cancelCeremony();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per mount
  }, []);

  return (
    <AuthScreen
      footer={
        <>
          {supported ? (
            <Button
              type="submit"
              form="login-form"
              variant="primary"
              block
              icon="key"
              loading={mutation.isPending}
            >
              {mutation.isPending ? m.login_pending() : m.login_button()}
            </Button>
          ) : (
            <Banner variant="error">{m.auth_unsupported()}</Banner>
          )}
          {mutation.isError && (
            <Banner variant="error">{m[passkeyErrorKey(mutation.error)]()}</Banner>
          )}
          {!recovering && (
            <Button variant="ghost" block onClick={() => setRecovering(true)}>
              {m.recover_open()}
            </Button>
          )}
          <p className="auth__note">{m.login_note()}</p>
        </>
      }
    >
      <div className="auth__brand">
        <BrandMark />
        <Wordmark />
      </div>
      <h1>
        <span className="visually-hidden">Dishly: </span>
        {m.login_tagline()}
      </h1>
      {supported && (
        <form
          id="login-form"
          className="auth__form"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <TextField
            label={m.login_autofill_label()}
            name="username"
            autoComplete="username webauthn"
            icon="key"
          />
        </form>
      )}
      {recovering && <RecoverForm />}
    </AuthScreen>
  );
}
